import React, { useState, useEffect } from "react";
import { Row, Col, Button, TitleUpdater } from "../components/ui/ot";
import styled from 'styled-components';
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faUser } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import ScrollButton from "./components/scroll_button"
// 定義 styled components
const Layout = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f0f2f5;
`;

const Sider = styled.div`
  width: ${({ collapsed }) => (collapsed ? '0' : '250px')}; 
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e6e6e6;
  display: flex;
  flex-direction: column;
  padding: ${({ collapsed }) => (collapsed ? '0' : '16px')};
  transition: width 0.3s ease, padding 0.3s ease;  
  position: relative;

  @media (max-width: 768px) {
    position: fixed;
    width: ${({ collapsed }) => (collapsed ? '0' : '70%')}; 
    height: 100vh;
    z-index: 1000;
    left: 0;
    top: 0;
    background: #ffffff;
  }
`;

const MainContent = styled.div`
  flex: 1;
  transition: margin-left 0.3s ease;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 768px) {
    margin-left: ${(props) => (props.isSidebarOpen ? (props.isCollapsed ? "60px" : "250px") : "0")};
  }
`;

const ExpandCollapseIcon = styled.span`
  margin-left: auto; 
  cursor: pointer;
  font-size: 16px; 
  color: ${({ themeColor }) => themeColor || '#0056d2'};  
`;

const SearchBox = styled.input`
  width: 100%;
  padding: 6px; 
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};

  @media (max-width: 768px) {
    margin-bottom: 10px; 
  }
`;

const TreeMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
  overflow-y: auto;  /* 保持 TreeMenu 本身的滾動行為 */
  height: calc(100vh - 100px);  /* 限制最高高度，避免超出 Sider 的可見範圍 */

  li {
    padding: 4px; 
    margin: 4px 0;
    border-radius: 8px;
    color: #595959;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    transition: background 0.3s, color 0.3s, cursor 0.3s;
    position: relative;

    &:hover {
      background: ${({ themeColor }) => `${themeColor}20` || '#f0f0f0'};
      cursor: pointer; 
    }

    &.active {
      background: ${({ themeColor }) => `${themeColor}20` || '#e6f7ff'};
      color: ${({ themeColor }) => themeColor || '#1890ff'};
      border-left: 4px solid ${({ themeColor }) => themeColor || '#1890ff'};
    }

    > ul {
      list-style: none;
      margin: 6px 0 0 8px;
      padding: 0;
      border-left: 1px solid #e6e6e6;

      li {
        padding: 4px 4px;
        margin: 2px 0;
        border-radius: 6px;
        font-size: 14px;
        color: #595959;

        &:hover {
          background: ${({ themeColor }) => `${themeColor}20` || '#e8e8e8'};
          cursor: pointer;
        }

        &.active {
          background: ${({ themeColor }) => `${themeColor}20` || '#e6f7ff'};
          color: ${({ themeColor }) => themeColor || '#1890ff'};
          border-left: 3px solid ${({ themeColor }) => themeColor || '#1890ff'};
        }
      }
    }
  }
`;

const ColorPickerButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background-color: ${({ color }) => color || '#0056d2'};
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 768px) { 
    width: 45px; 
    height: 45px; 
  }
`;

const ColorInput = styled.input`
  position: absolute; 
  bottom: 60px;  
  right: 5px; 
  border: none; 
  width: 150px; 
  height: 30px; 
  cursor: pointer;
  opacity: 0; 
  z-index: 10; 

  @media (max-width: 768px) { 
    width: 120px; 
    height: 25px; 
  }
`;

const LayoutBackend = ({
    prefixTitle = "",                   //系統名稱
    menuData = [],                      //後台選單列
    searchBoxMode = false,              //Header搜尋框開關控制
    selfSettingButtonMode = false,      //個人資料設定開關控制
    selfSettingRoute = '',              //個人資料設定Route
    ColorPickerButtonMode = false,      //系統後台色調開關控制
    titleUpdaterOpen = false,           //系統根據路徑以及language 改變 title
}) => {
    const navigate = useNavigate();
    const location = useLocation();  // 獲取當前路徑
    const [selectedKey, setSelectedKey] = useState(location.pathname);  // 將選中項目設置為當前路徑
    const [collapsed, setCollapsed] = useState(false);  // 用於電腦版的側邊欄控制
    const [openKeys, setOpenKeys] = useState([]);
    const [themeColor, setThemeColor] = useState('#32517B');
    const [auth, setAuth] = useState({ name: "", role: "" });
    const [isMobileVisible, setIsMobileVisible] = useState(false);  // 用於手機版的側邊欄顯示控制
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // 遍歷 menuData，提取所有有子選單的鍵
    const getAllKeys = (items) => {
        let keys = [];
        items.forEach((item) => {
            if (item.children) {
                keys.push(item.key || item.label);
                keys = [...keys, ...getAllKeys(item.children)];
            }
        });
        return keys;
    };

    const toggleItem = (key) => {
        setOpenKeys((prevOpenKeys) => {
            if (prevOpenKeys.includes(key)) {
                return prevOpenKeys.filter((openKey) => openKey !== key);
            } else {
                return [...prevOpenKeys, key];
            }
        });
    };

    const renderMenuItems = (items) => {
        return items.map((item) => {
            const hasChildren = Array.isArray(item.children);
            const uniqueKey = item.key || item.label;

            return (
                <>
                    <li
                        key={uniqueKey}
                        className={`${selectedKey === item.key ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation(); 
                            toggleItem(uniqueKey);
                            handleMenuClick(item)
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {item.label}
                            {hasChildren && (
                                <ExpandCollapseIcon themeColor={themeColor} onClick={(e) => { e.stopPropagation(); toggleItem(uniqueKey); }}>
                                    {openKeys.includes(uniqueKey) ? '▼' : '▶'}
                                </ExpandCollapseIcon>
                            )}
                        </div>
                        {hasChildren && openKeys.includes(uniqueKey) && (
                            <ul>{renderMenuItems(item.children)}</ul>
                        )}
                    </li>
                </>
            );
        });
    };

    const handleMenuClick = (item) => {
        if (item.key) {
            setSelectedKey(item.key);
            navigate(item.key);
        }
    };

    const handleColorChange = (e) => {
        const selectedColor = e.target.value;
        setThemeColor(selectedColor);
    };

    const toggleSider = () => {
        if (windowWidth > 768) {
            setCollapsed(!collapsed); // 電腦版切換側邊欄
        } else {
            setIsMobileVisible(!isMobileVisible); // 手機版切換側邊欄
        }
    };

    const get_system_user = () => {
        axios.get('/system/user')
            .then(response => {
                let role_info = response.data[0] || {};
                let module_name = response.data.map(value => value.module_name).join(',');
                setAuth({ name: role_info.name, role: module_name });
            })
            .catch(console.error);
    };

    const LoginOut = () => {
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('lineBind');
        axios.post('/logout').then(() => navigate("/login")).catch(console.error);
    };

    // 監聽視窗大小變化
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        // 清理事件監聽器
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // 根據視窗寬度自動切換狀態
    useEffect(() => {
        if (windowWidth > 768) {
            setIsMobileVisible(false);  // 當切換到電腦版時自動隱藏手機版側邊欄
        }
    }, [windowWidth]);

    // 根據當前路徑展開父選單
    useEffect(() => {
        // const parentKeys = findParentKeys(menuData, location.pathname);  // 找到當前頁面的父選單
        // setOpenKeys(parentKeys);  // 直接設置為當前頁面的父選單
        setSelectedKey(location.pathname);  // 設置當前選中的選單項目
    }, [location.pathname, menuData]);

    useEffect(() => {
        const allKeys = getAllKeys(menuData);  // 獲取所有有子選單的鍵
        setOpenKeys(allKeys);  // 設置所有鍵為展開狀態
    }, [menuData]);

    useEffect(() => {
        axios.get("/api/auth/me")
            .then((response) => {
                const { name, role } = response.data;
                setAuth({ name, role });
            })
            .catch((error) => {
                console.error("Failed to fetch user info:", error);
            });
    }, []);

    useEffect(() => {
        get_system_user();
    }, []);

    return (
        <Layout>
            {titleUpdaterOpen && <TitleUpdater website_name={prefixTitle} />}
            <Sider
                collapsed={collapsed}
                style={{
                    display: isMobileVisible || windowWidth > 768 ? 'block' : 'none',
                    position: 'fixed',
                    width: isMobileVisible ? '70%' : (collapsed ? '0' : '250px'),
                    transition: 'width 0.3s ease',
                }}
            >
                <Row gutter={[8, 8]} style={{
                    display: 'flex', alignItems: 'center', justify: 'space-around',
                }}>
                    {windowWidth < 769 &&
                        <>
                            <Col span={20} style={{ display: "flex", justifyContent: "left", fontWeight: 'bold', fontSize: "6vmin" }}>
                                <span style={{ color: themeColor, fontWeight: 'bold' }}>{prefixTitle}</span>
                            </Col>
                            <Col span={4}>
                                <Button style={{ display: "flex", justifyContent: "center", backgroundColor: themeColor, fontWeight: 'bold' }} onClick={toggleSider}>
                                    {!collapsed && 'X'}
                                </Button>
                            </Col>
                        </>
                    }
                    {windowWidth > 768 && !collapsed &&
                        <>
                            <Col span={24} style={{ display: "flex", justifyContent: "center", fontWeight: 'bold', fontSize: "3vmin" }}>
                                <span style={{ color: themeColor, fontWeight: 'bold' }}>{prefixTitle}</span>
                            </Col>
                        </>
                    }

                    <Col span={24}>
                        {isMobileVisible && searchBoxMode &&
                            <SearchBox placeholder="智慧檢索..." collapsed={collapsed} />
                        }
                        <TreeMenu themeColor={themeColor}>
                            {renderMenuItems(menuData)}
                        </TreeMenu>
                        {isMobileVisible &&
                            <Row gutter={[8, 8]}>
                                <Col span={24}>
                                    <Row gutter={[8, 8]}>
                                        <Col>
                                            <FontAwesomeIcon icon={faUser} size="lg" />
                                        </Col>
                                        <Col>
                                            <span style={{ marginRight: '16px', color: themeColor }}>
                                                {auth.name || "使用者名稱"}
                                            </span>
                                        </Col>
                                    </Row>
                                </Col>
                                {selfSettingButtonMode &&
                                    <Col span={12}>
                                        <Button style={{ backgroundColor: themeColor, width: '100%', }} onClick={() => {
                                            navigate(selfSettingRoute)
                                        }}>個人資料設定</Button>
                                    </Col>
                                }
                                <Col span={12}>
                                    <Button style={{ backgroundColor: `${themeColor}80`, width: '100%', color: themeColor }} onClick={LoginOut}>登出</Button>
                                </Col>
                            </Row>
                        }

                    </Col>

                </Row>

            </Sider>

            <MainContent
                style={{
                    marginLeft: windowWidth > 768 && !collapsed ? '250px' : '0',
                    transition: 'margin-left 0.3s ease',
                }}
            >
                <Row
                    gutter={[8, 8]}
                    style={{
                        alignItems: 'center',
                        padding: '16px',
                        background: '#ffffff',
                        justify: 'space-around',
                        zIndex: 1000, /* 確保它在其他內容上方 */
                    }}
                >
                    {windowWidth < 769 &&
                        <>
                            <Col span={4}>
                                <Button style={{ display: "flex", justifyContent: "center", backgroundColor: themeColor, fontWeight: 'bold' }} onClick={toggleSider}>
                                    {!collapsed && '☰'}

                                </Button>

                            </Col>
                            <Col span={16} style={{ display: "flex", justifyContent: "center", fontWeight: 'bold', fontSize: "6.5vmin" }}>
                                <span style={{ color: themeColor, fontWeight: 'bold' }}>{prefixTitle}</span>
                            </Col>
                        </>
                    }
                    {windowWidth > 768 &&
                        <>
                            <Col span={6}>
                                <Button style={{ display: "flex", justifyContent: "left", backgroundColor: themeColor, fontWeight: 'bold' }} onClick={toggleSider}>
                                    {collapsed ? '☰' : '<'}
                                </Button>

                            </Col>
                            {searchBoxMode &&
                                <Col span={6} style={{ display: "flex", justifyContent: "right" }}>
                                    <SearchBox placeholder="智慧檢索..." />
                                </Col>
                            }
                            {!collapsed &&
                                <Col span={!searchBoxMode ? 18 : 12}>
                                    <Row gutter={[8, 8]} style={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
                                        {/* <Col>
                                            <Row gutter={[8, 8]}> */}
                                             <Col>
                                            <Button style={{ backgroundColor: themeColor, width: '100%', }} 
                                            onClick={() => {
                                                window.open('https://docs.google.com/document/d/1eCEtfH1FeGKqbA2FnmhN6OKNuzGOW_Zm1mJdzksp87w/edit?tab=t.v60axx7umnd6','blank')
                                            }}>操作說明</Button>
                                        </Col>
                                        <Col>
                                            <Button style={{ backgroundColor: themeColor, width: '100%', }} onClick={() => {
                                                window.open('https://docs.google.com/document/d/1pl_cJZwvOEdRTtNHuPdMuEuTKioLUN1DOXwemNQjtPk/edit?usp=sharing','blank')
                                            }}>系統更新回饋</Button>
                                        </Col>
                                        <Col>
                                            <FontAwesomeIcon icon={faUser} size="lg" />
                                        </Col>
                                        <Col>
                                            <span style={{ marginRight: '16px', color: themeColor }}>
                                                {auth.name || "使用者名稱"}
                                            </span>
                                        </Col>
                                        {/* </Row>
                                        </Col> */}
                                        {selfSettingButtonMode &&
                                            <Col>
                                                <Button style={{ backgroundColor: themeColor, width: '100%', }} onClick={() => {
                                                    navigate(selfSettingRoute)
                                                }}>個人資料設定</Button>
                                            </Col>
                                        }
                                        <Col>
                                            <Button style={{ backgroundColor: `${themeColor}80`, width: '100%', color: themeColor }} onClick={LoginOut}>登出</Button>
                                        </Col>
                                    </Row>
                                </Col>
                            }
                        </>
                    }
                </Row>
                <Row
                    gutter={[8, 8]}
                    justify="space-around"
                    style={{ 
                        padding: "20px",
                        background: '#ffffff',
                     }}
                >
                    <Col span={24}>
                        <Outlet />
                    </Col>
                </Row>
            </MainContent>
            {ColorPickerButtonMode &&
                <ColorPickerButton color={themeColor} onClick={() => document.getElementById('color-input').click()}>
                    <FontAwesomeIcon icon={faCog} style={{ color: 'white' }} />
                    <ColorInput
                        id="color-input"
                        type="color"
                        onChange={handleColorChange}
                        value={themeColor}
                    />
                </ColorPickerButton>
            }
            <ScrollButton color={themeColor}></ScrollButton>
        </Layout>
    );
};

export default LayoutBackend;


axios.interceptors.request.use(
    async (config) => {
        const access_token = localStorage.getItem("access_token");

        if (access_token) {
            config.headers = {
                ...config.headers,
                authorization: `Bearer ${access_token}`,
            };
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axios.interceptors.response.use(
    response => {
        return response
    },
    function (error) {
        const access_token_url = '/access_token';
        const config = error.config
        if (
            error.response?.status === 401 &&
            config.url === access_token_url
        ) {
            // window.location.href = ('/login')
            return Promise.reject(error)
        }

        if (error.response?.status === 401 && !config._retry) {
            config._retry = true
            const refreshToken = localStorage.getItem('refresh_token')
            return axios
                .post(access_token_url, {
                    refresh_token: refreshToken
                })
                .then(res => {
                    if (res.status === 201) {
                        localStorage.setItem('access_token', res.data.access_token);
                        config.headers = {
                            ...config.headers,
                            authorization: `Bearer ${localStorage.getItem('access_token')}`,
                        };
                        return axios(config)
                    }
                })
        }
        return Promise.reject(error)
    }
)
